/**
 * Landing image slots — bundled defaults + admin overrides.
 *
 * Every editable picture on the landing page is addressed by a stable slot key
 * rather than an import. The bundled art below is the fallback; when an admin
 * uploads a replacement, the API returns an override for that slot and it wins.
 *
 * The hero and the merch grid are intentionally absent: the hero's art is
 * fixed, and merch imagery becomes Shopify catalogue data.
 *
 * Keep the keys in sync with `LANDING_IMAGE_SLOTS` in
 * `src/api/features/landing/landing.types.ts`.
 */

import elfLogo from '../../../../assets/elf/elfcircle-logo.png';
import eventsWall from '../../../../assets/elf/events-wall.jpg';
import fashionShow from '../../../../assets/elf/fashion-show.png';
import filmFestival from '../../../../assets/elf/film-festival.png';
import musicFestival from '../../../../assets/elf/music-festival.png';
import serviceEvents from '../../../../assets/elf/service-events.jpg';
import serviceMarketing from '../../../../assets/elf/service-marketing.jpg';
import serviceMerch from '../../../../assets/elf/service-merch.jpg';
import serviceWeb from '../../../../assets/elf/service-web.jpg';
import schedulerCalendar from '../../../../assets/elf/scheduler-calendar.jpg';
import shopStorefront from '../../../../assets/elf/shop-storefront.jpg';
import socialPhones from '../../../../assets/elf/social-phones.jpg';
import sponsorsNetwork from '../../../../assets/elf/sponsors-network.jpg';
import streetCulture from '../../../../assets/elf/street-culture.png';

export interface LandingSlotSpec {
  /** Human label shown in the admin editor. */
  label: string;
  /** Bundled art used until an override exists. */
  fallback: string;
  /** Guidance shown in the upload dialog — shape the art should be. */
  hint: string;
}

export const LANDING_IMAGE_SLOTS = {
  'brand.logo': { label: 'Brand logo', fallback: elfLogo, hint: 'Transparent PNG, wide' },
  'service.marketing': { label: 'Service — Marketing', fallback: serviceMarketing, hint: 'Landscape' },
  'service.web': { label: 'Service — Web Development', fallback: serviceWeb, hint: 'Landscape' },
  'service.events': { label: 'Service — Events', fallback: serviceEvents, hint: 'Landscape' },
  'service.merch': { label: 'Service — Merch', fallback: serviceMerch, hint: 'Landscape' },
  'scheduler.banner': { label: 'Scheduler banner', fallback: schedulerCalendar, hint: 'Wide banner' },
  'events.backdrop': { label: 'Events backdrop', fallback: eventsWall, hint: 'Landscape, dark' },
  'event.neon-circuit': { label: 'Event — Neon Circuit', fallback: musicFestival, hint: 'Poster, 3:4' },
  'event.cut-thread': { label: 'Event — Cut & Thread', fallback: fashionShow, hint: 'Poster, 3:4' },
  'event.frame-rate': { label: 'Event — Frame Rate', fallback: filmFestival, hint: 'Poster, 3:4' },
  'event.block-party': { label: 'Event — Block Party', fallback: streetCulture, hint: 'Poster, 3:4' },
  'shop.backdrop': { label: 'Shop backdrop', fallback: shopStorefront, hint: 'Landscape, dark' },
  'social.backdrop': { label: 'Social backdrop', fallback: socialPhones, hint: 'Landscape, dark' },
  'sponsors.backdrop': { label: 'Sponsors backdrop', fallback: sponsorsNetwork, hint: 'Landscape, dark' },
  'sponsors.case': { label: 'Sponsors case study', fallback: musicFestival, hint: 'Landscape' },
  'footer.backdrop': { label: 'Footer backdrop', fallback: musicFestival, hint: 'Landscape, dark' },
} satisfies Record<string, LandingSlotSpec>;

/* -------------------------------------------------------------------------- */
/* Social wall                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Four platform tabs, each with a featured tile and a six-up grid — 28 slots.
 * Generated rather than listed: a hand-maintained literal map that size drifts
 * out of sync with the UI, and the shape is perfectly regular.
 */
export const SOCIAL_PLATFORMS = ['instagram', 'tiktok', 'youtube', 'x'] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export const SOCIAL_POSTS = ['featured', 'post1', 'post2', 'post3', 'post4', 'post5', 'post6'] as const;
export type SocialPostId = (typeof SOCIAL_POSTS)[number];

export type SocialSlot = `social.${SocialPlatform}.${SocialPostId}`;

const SOCIAL_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  x: 'X',
};

/** Rotating stand-ins so a fresh install doesn't show the same photo 28 times. */
const SOCIAL_FALLBACKS = [musicFestival, fashionShow, filmFestival, streetCulture];

const SOCIAL_SLOTS = Object.fromEntries(
  SOCIAL_PLATFORMS.flatMap((platform, p) =>
    SOCIAL_POSTS.map((post, i) => [
      `social.${platform}.${post}`,
      {
        label: `${SOCIAL_LABELS[platform]} — ${post === 'featured' ? 'featured' : `post ${i}`}`,
        fallback: SOCIAL_FALLBACKS[(p + i) % SOCIAL_FALLBACKS.length],
        hint: post === 'featured' ? 'Portrait, 4:5' : 'Square',
      } satisfies LandingSlotSpec,
    ]),
  ),
) as Record<SocialSlot, LandingSlotSpec>;

export type LandingSlot = keyof typeof LANDING_IMAGE_SLOTS | SocialSlot;

/** Every slot, fixed and generated. */
export const ALL_LANDING_SLOTS: Record<string, LandingSlotSpec> = {
  ...LANDING_IMAGE_SLOTS,
  ...SOCIAL_SLOTS,
};

export const LANDING_SLOT_KEYS = Object.keys(ALL_LANDING_SLOTS) as LandingSlot[];

export function slotFallback(slot: LandingSlot): string {
  return ALL_LANDING_SLOTS[slot]?.fallback ?? musicFestival;
}

export function slotSpec(slot: LandingSlot): LandingSlotSpec {
  return ALL_LANDING_SLOTS[slot] ?? { label: slot, fallback: musicFestival, hint: '' };
}
